import { inject, Injectable } from '@angular/core';
import { LocalStorageService } from '@capturum/ui/api';
import { defaultStepOptions, nonModalPageTours, pageTourDefs, TourStepDef } from '@core/configs/tour.config';
import { TranslateService } from '@ngx-translate/core';
import { ShepherdService } from 'angular-shepherd';
import { StepOptions } from 'shepherd.js';

/** Actions a page can expose so tour steps can drive its UI (e.g. open a panel). */
export type TourActions = Record<string, () => void | Promise<void>>;

@Injectable({ providedIn: 'root' })
export class TourService {
  readonly #shepherd = inject(ShepherdService);
  readonly #translate = inject(TranslateService);
  readonly #localStorage = inject(LocalStorageService);

  // startForPage shows a page's first-visit tour once. Later visits are skipped
  // via a per-page localStorage flag (backend persistence isn't ready yet).
  // `actions` lets steps open panels etc. before they attach (see TourStepDef.beforeShow).
  startForPage(pageKey: string, actions: TourActions = {}): void {
    const defs = pageTourDefs[pageKey];

    if (!defs?.length || this.hasSeen(pageKey) || this.#shepherd.isActive) {
      return;
    }

    this.#shepherd.defaultStepOptions = defaultStepOptions;
    this.#shepherd.modal = !nonModalPageTours.includes(pageKey);
    // Fires on both complete and cancel, so a dismissed tour still counts as seen.
    // Must also clear isActive: overriding onTourFinish replaces angular-shepherd's
    // own handler, which is what resets isActive (otherwise later tours stay blocked).
    this.#shepherd.onTourFinish = () => {
      this.#shepherd.isActive = false;
      this.#markSeen(pageKey);
    };
    // One tour at a time. ShepherdService is a singleton and appends steps;
    // when a second page tour is added, reset its steps before addSteps.
    this.#shepherd.addSteps(defs.map((def, index) => this.#toStep(def, index, defs.length, actions)));
    this.#shepherd.start();
  }

  // Advance a tour that is waiting on an external action (e.g. a submitted dialog).
  next(): void {
    if (this.#shepherd.isActive) {
      this.#shepherd.next();
    }
  }

  cancel(): void {
    if (this.#shepherd.isActive) {
      this.#shepherd.cancel();
    }
  }

  hasSeen(pageKey: string): boolean {
    return !!this.#localStorage.getItem(this.#seenKey(pageKey));
  }

  #markSeen(pageKey: string): void {
    this.#localStorage.setItem(this.#seenKey(pageKey), true);
  }

  #seenKey(pageKey: string): string {
    return `tourSeen:${pageKey}`;
  }

  #toStep(def: TourStepDef, index: number, total: number, actions: TourActions): StepOptions {
    const isLast = index === total - 1;

    const backButton =
      index > 0
        ? [
            {
              classes: 'back-button',
              text: this.#translate.instant('button.back'),
              action: () => this.#shepherd.back(),
            },
          ]
        : [];

    // A step waiting on an external action (e.g. submitting a dialog) shows no
    // Next/Finish button; the page advances it via TourService.next().
    const primaryButton = def.waitForExternal
      ? []
      : [
          {
            classes: isLast ? 'finish-button' : 'next-button',
            secondary: false,
            text: this.#translate.instant(isLast ? 'button.finish' : 'button.next'),
            action: () => (isLast ? this.#shepherd.complete() : this.#shepherd.next()),
          },
        ];

    const step: StepOptions = {
      id: def.id,
      attachTo: def.attachTo,
      title: this.#translate.instant(def.titleKey),
      text: this.#translate.instant(def.textKey),
      buttons: [...backButton, ...primaryButton],
    };

    // Run the optional page action (e.g. open the config panel), then wait for the
    // target element to exist before shepherd shows the step.
    if (def.attachTo?.element) {
      const runAction = def.beforeShow ? actions[def.beforeShow] : undefined;

      step.beforeShowPromise = async () => {
        await runAction?.();
        await this.#waitForElement(def.attachTo.element as string);
      };
    }

    return step;
  }

  #waitForElement(selector: string, timeoutMs = 5000): Promise<void> {
    return new Promise((resolve) => {
      const start = Date.now();
      const poll = () => {
        if (document.querySelector(selector) || Date.now() - start > timeoutMs) {
          resolve();

          return;
        }
        setTimeout(poll, 50);
      };

      poll();
    });
  }
}
